import React, { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import i18n from '../../i18n';
import type { MenuStoreTemplateMenuRow } from '../../models/menu-store.entity';
import { MenuLibraryAddDishDialogContent } from '../menu-library-management/menu-library-add-dish-dialog-content.component';
import { MenuLibraryManagementState } from '../menu-library-management/menu-library-management-state';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import MainPageState from '../main/main-page-states';
import { menuStoreTemplateRowToMenuDish } from './adapters/menu-store-dish-detail.adapter';
import { MenuStoreCommonDialog } from './menu-store-common-dialog.component';
import { MenuStoreDishDetailBrowseRestricted } from './menu-store-dish-detail-browse-restricted.component';
import {
    resolveMenuStoreBrowseDishDetailVariant,
    resolveMenuStoreDishDetailPageKind,
} from './menu-store.helpers';

type MenuStoreDishDetailDialogProps = {
    /** 弹窗开关。 */
    open: boolean;
    /** 当前选中菜品（模板 structure 行）。 */
    dish: MenuStoreTemplateMenuRow | null;
    /** 关闭弹窗回调。 */
    onClose: () => void;
    /** 当前门店 string_id，用于与菜单库同源的下拉参考接口（税组、打印类等）。 */
    restaurantStringId?: string;
    /** 菜单详情来源：商店浏览未激活时走浏览态详情版式 */
    detailSource: 'store-home' | 'my-activated' | 'my-uploaded';
};

/**
 * 菜单商店菜品详情：已激活/我上传复用菜单库只读表单；商店浏览（未激活）用统一版式，按 `is_detail_visible` 区分开放/不开放。
 */
export function MenuStoreDishDetailDialog(props: MenuStoreDishDetailDialogProps): React.ReactElement | null {
    const { open, dish, onClose, restaurantStringId, detailSource } = props;
    /** 与菜单库弹窗共用组件所需的轻量 state（仅解析 rbp、不参与保存）。 */
    const menuLibraryStubState = useLocalObservable(() => new MenuLibraryManagementState());
    const mainPageData = MobxObjectsManager.get(MainPageState);

    /** 顶栏当前餐厅同步到 stub，保证 `resolveRbpStringIdForApi` 与菜单库一致。 */
    useEffect(() => {
        const r = mainPageData.currentRestaurant;
        runInAction(() => {
            menuLibraryStubState.menuLibraryRestaurant = r ?? null;
        });
    }, [mainPageData.currentRestaurant, menuLibraryStubState]);

    /** 全量详情与浏览态详情的分支 */
    const pageKind = useMemo(
        () => (open && dish ? resolveMenuStoreDishDetailPageKind(detailSource) : 'full'),
        [open, dish, detailSource],
    );

    const menuDish = useMemo(() => {
        if (!dish || pageKind !== 'full') {
            return null;
        }
        return menuStoreTemplateRowToMenuDish(dish);
    }, [dish, pageKind]);

    /** 切换行或字段变化时触发菜单库表单重新 hydrate */
    const standaloneHydrateKey = useMemo(() => {
        if (!dish || pageKind !== 'full') {
            return '';
        }
        return [
            dish.item_name1 ?? '',
            dish.item_name2 ?? '',
            dish.nlu ?? '',
            String(dish.price_1 ?? ''),
            String(dish.takeout_price_1 ?? ''),
            (dish.allergen ?? []).join(','),
            dish.image_url ?? '',
            String(dish.dish_image ?? ''),
            String(dish.dish_image_small ?? ''),
        ].join('|');
    }, [dish, pageKind]);

    if (!open || !dish) {
        return null;
    }

    const rbpForRefs = restaurantStringId?.trim() || mainPageData.currentRestaurant?.string_id?.trim() || undefined;

    if (pageKind === 'browse') {
        /** 未激活：同版式，`locked` 为图模糊 + 敏感字段占位 */
        const browseVariant = resolveMenuStoreBrowseDishDetailVariant(dish);
        return (
            <MenuStoreCommonDialog
                title={i18n.t('menu_library_detail_dish_dialog_title')}
                onClose={onClose}
                dishDetailLayout
                dialogClassName="menu-store-dish-detail-dialog"
                hideTitleIcon
                hideBottomClose
            >
                <Box
                    className="menu-store-dish-detail-dialog__content menu-store-dish-detail-dialog__content--browse"
                    sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}
                >
                    <MenuStoreDishDetailBrowseRestricted dish={dish} variant={browseVariant} />
                </Box>
            </MenuStoreCommonDialog>
        );
    }

    if (!menuDish) {
        return null;
    }

    return (
        <MenuStoreCommonDialog
            title={i18n.t('menu_library_detail_dish_dialog_title')}
            onClose={onClose}
            dishDetailLayout
            dialogClassName="menu-store-dish-detail-dialog"
            hideTitleIcon
            hideBottomClose
        >
            <Box className="menu-store-dish-detail-dialog__content" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <MenuLibraryAddDishDialogContent
                    state={menuLibraryStubState}
                    onClose={onClose}
                    standaloneReadonlyDish={menuDish}
                    standaloneHydrateKey={standaloneHydrateKey}
                    standaloneRbpStringId={rbpForRefs}
                />
            </Box>
        </MenuStoreCommonDialog>
    );
}
